// hooks/useDraggableVideo.js
import { useState, useEffect, useRef } from "react";

export const useDraggableVideo = (videoRef, isMobile, simulationType) => {
  const [position, setPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const startPos = useRef({ x: 0, y: 0 });
  const EDGE_MARGIN = 16;

  const getClient = (event) => {
    if (event.touches && event.touches.length > 0) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  };

  const handleDown = (event) => {
    const { x, y } = getClient(event);
    setIsDragging(true);
    startPos.current = { x: x - position.x, y: y - position.y };
  };

  useEffect(() => {
    if (!isDragging) return; // Only listen to window events when actively dragging

    const handleMove = (event) => {
      const { x, y } = getClient(event);
      setPosition({ x: x - startPos.current.x, y: y - startPos.current.y });
    };

    const handleUp = () => {
      setIsDragging(false);
      // --- Snapping Logic ---
      const videoEl = videoRef.current;
      if (!videoEl) return;

      const videoWidth = 182;
      const videoHeight = 165;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (isMobile) {
        let mobWidth = 103;
        let mobHeight = simulationType === "feedback_session" ? 93 : 96;

        const corners = [
          { x: EDGE_MARGIN, y: EDGE_MARGIN },
          { x: vw - mobWidth - EDGE_MARGIN, y: EDGE_MARGIN },
          { x: EDGE_MARGIN, y: vh - (mobHeight + 70) - EDGE_MARGIN },
          {
            x: vw - mobWidth - EDGE_MARGIN,
            y: vh - (mobHeight + 70) - EDGE_MARGIN,
          },
        ];

        // Find closest and set
        let closest = corners.reduce((prev, curr) =>
          Math.hypot(curr.x - position.x, curr.y - position.y) <
          Math.hypot(prev.x - position.x, prev.y - position.y)
            ? curr
            : prev,
        );
        setPosition(closest);
      } else {
        const containerEl = document.getElementById("callscreen-container");
        if (!containerEl) return;

        const {
          width: cWidth,
          height: cHeight,
          left: cLeft,
          top: cTop,
        } = containerEl.getBoundingClientRect();

        const corners = [
          { x: EDGE_MARGIN, y: EDGE_MARGIN },
          { x: cWidth - videoWidth - EDGE_MARGIN, y: EDGE_MARGIN },
          { x: EDGE_MARGIN, y: cHeight - videoHeight - EDGE_MARGIN },
          {
            x: cWidth - videoWidth - EDGE_MARGIN,
            y: cHeight - videoHeight - EDGE_MARGIN,
          },
        ].map((c) => ({
          x: Math.max(0, Math.min(c.x, cWidth - videoWidth)),
          y: Math.max(0, Math.min(c.y, cHeight - videoHeight)),
        }));

        const relativeX = position.x - cLeft;
        const relativeY = position.y - cTop;

        let closest = corners.reduce((prev, curr) =>
          Math.hypot(curr.x - relativeX, curr.y - relativeY) <
          Math.hypot(prev.x - relativeX, prev.y - relativeY)
            ? curr
            : prev,
        );
        setPosition(closest);
      }
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging, position, isMobile, simulationType, videoRef]);

  return { position, isDragging, handleDown };
};
